<script setup>
import { computed } from 'vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import MediaflowDock from './MediaflowDock.vue'
import MediaflowMenu from './MediaflowMenu.vue'
import MediaflowDockEmpty from './MediaflowDockEmpty.vue'
import MediaflowSceneLimit from './MediaflowSceneLimit.vue'
import MediaflowSourcesTip from './MediaflowSourcesTip.vue'
import { DOCK_DANGER_ITEM, sceneLimitReached, sceneLimitReason } from './mediaflowMeta'

/**
 * OBS "Scenes" dock: every layout of the production (breaks live in their own
 * dock), as plain rows — number, name, status pill; no thumbnails. Click = load into Preview, double-click = send to Program. Number
 * badges match the 1–9 hotkeys. On phones a tap asks to send it to live,
 * since there is no Preview monitor there.
 */
const props = defineProps({
	desk: { type: Object, required: true },
	enabled: { type: Boolean, default: true },
	sessionActive: { type: Boolean, default: false },
	/** Plan scene limit; scenes and breaks count toward it together. */
	maxFlows: { type: Number, default: 0 },
	flowCount: { type: Number, default: 0 },
	switchingFlowId: { type: String, default: null },
	mobile: { type: Boolean, default: false },
	/** Pop-out: management actions stay in the main window. */
	readOnly: { type: Boolean, default: false },
})

const emit = defineEmits(['select', 'send-to-live', 'add', 'rename', 'duplicate', 'delete', 'move'])

const scenes = computed(() => props.desk.layouts.value)
const previewId = computed(() => props.desk.editingFlowId.value)

const tally = (flow) => {
	const inPreview = flow._id === previewId.value && !props.mobile
	const onProgram = !!flow.active
	if (inPreview && onProgram) return 'both'
	if (onProgram) return 'program'
	if (inPreview) return 'preview'
	return ''
}
/** Red only while a session runs; before that the Program scene is just cued. */
const programTone = computed(() => (props.sessionActive ? 'bg-error-transparent ring-1 ring-error' : 'bg-surface-3 ring-1 ring-surface-6'))
const programLabel = computed(() => (props.sessionActive ? 'ON AIR' : 'CUED'))
const rowClass = (flow) => ({
	program: programTone.value,
	both: programTone.value,
	preview: 'bg-success-transparent ring-1 ring-success',
	'': '',
}[tally(flow)])
const sourceCount = (flow) => (Array.isArray(flow.media) ? flow.media.length : 0)
const countLabel = (flow) => {
	const n = sourceCount(flow)
	return n === 0 ? 'No sources' : `${n} ${n === 1 ? 'source' : 'sources'}`
}
const isEmpty = (flow) => sourceCount(flow) === 0
const rowTitle = (flow, i) => (props.mobile
	? `${countLabel(flow)} · Tap to send ${flow.name} to live`
	: `${countLabel(flow)} · Click: preview · Double-click: send to live${i < 9 ? ` · Key ${i + 1}` : ''}`)

const rowHint = (i) => `Click: preview · Double-click: send to live${i < 9 ? ` · Key ${i + 1}` : ''}`
/** The flow as Preview shows it (unapplied edits included). */
const draftedOf = (flow) => props.desk.draftedFlows?.value?.find((f) => f._id === flow._id) || flow

const atLimit = computed(() => sceneLimitReached(props.flowCount, props.maxFlows))
const addReason = computed(() => {
	if (!props.enabled) return 'Enable the production first'
	if (atLimit.value) return sceneLimitReason(props.flowCount, props.maxFlows)
	return ''
})

const onClick = (flow) => {
	if (props.mobile) emit('send-to-live', flow)
	else emit('select', flow)
}
const onDblClick = (flow) => {
	if (!props.mobile) emit('send-to-live', flow)
}
</script>

<template>
	<MediaflowDock
		title="Scenes"
		:count="String(scenes.length)"
		:count-label="`${scenes.length} ${scenes.length === 1 ? 'scene' : 'scenes'}`"
		testid="dock-scenes"
		:add-label="readOnly ? '' : 'Add scene'"
		:add-disabled-reason="addReason"
		@add="emit('add')"
	>
		<template v-if="maxFlows > 0 && !readOnly" #header-extra>
			<MediaflowSceneLimit :used="flowCount" :max="maxFlows" :reached="atLimit" testid="dock-scenes-usage" />
		</template>
		<template v-if="atLimit && !readOnly" #footer>
			<MediaflowSceneLimit variant="notice" :used="flowCount" :max="maxFlows" reached testid="dock-scenes-limit" />
		</template>

		<MediaflowDockEmpty v-if="!scenes.length" text="No scenes yet." :action-label="readOnly || atLimit ? '' : 'Add scene'" :disabled-reason="readOnly || atLimit ? '' : addReason" @add="emit('add')" />
		<ul v-else class="m-0 flex list-none flex-col gap-1.5 p-0" role="list">
			<li
				v-for="(flow, i) in scenes"
				:key="flow._id"
				class="mf-row group relative flex h-14 items-center rounded-lg pr-1"
				:class="rowClass(flow)"
				:data-tone="tally(flow) || undefined"
				data-testid="scene-row"
				:data-flow-id="flow._id"
				:data-tally="tally(flow) || undefined"
			>
				<MediaflowSourcesTip :flow="flow" :drafted="draftedOf(flow)" :hint="rowHint(i)" :disabled="mobile" wrapper-class="flex min-w-0 flex-1 self-stretch">
				<button
					type="button"
					class="flex min-w-0 flex-1 items-center gap-2 self-stretch rounded-lg py-2 pl-2 pr-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
					:aria-label="`${flow.name}${flow.active ? (sessionActive ? ', on Program' : ', cued to start the stream') : ''}${tally(flow) === 'preview' || tally(flow) === 'both' ? ', in Preview' : ''}`"
					:aria-current="flow._id === previewId ? 'true' : undefined"
					:title="mobile ? rowTitle(flow, i) : undefined"
					data-testid="scene-select"
					@click="onClick(flow)"
					@dblclick="onDblClick(flow)"
				>
					<span v-if="!mobile" class="w-4 shrink-0 text-center font-mono text-[0.7rem] tabular-nums text-surface-7" aria-hidden="true">{{ i < 9 ? i + 1 : '' }}</span>
					<span class="flex min-w-0 flex-1 flex-col gap-1">
						<span class="truncate text-[0.8rem] font-medium leading-snug" :class="isEmpty(flow) ? 'text-surface-8' : 'text-surface-text-2'">{{ flow.name }}</span>
						<span v-if="flow.active || tally(flow) === 'preview' || tally(flow) === 'both' || isEmpty(flow) || switchingFlowId === flow._id" class="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[0.7rem] leading-4 text-surface-8">
							<Tooltip v-if="flow.active && isEmpty(flow)" :content="sessionActive ? 'This scene has no sources, so viewers see black' : 'This scene has no sources, so viewers will see black when you go live'" wrapper-class="inline-flex" popper-class="max-w-[240px] text-sm break-words">
								<span class="inline-flex h-4 shrink-0 items-center gap-1 whitespace-nowrap rounded bg-gold px-1.5 text-[0.58rem] font-semibold leading-none tracking-wider text-surface-darker" data-testid="scene-black-warning">
									<span class="size-1 rounded-full bg-surface-darker" aria-hidden="true" />{{ programLabel }} · BLACK SCREEN
								</span>
							</Tooltip>
							<span
								v-else-if="flow.active"
								class="inline-flex h-4 shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 text-[0.58rem] font-semibold leading-none tracking-wider"
								:class="sessionActive ? 'bg-error text-white' : 'bg-surface-5 text-surface-text-2'"
								:data-live="sessionActive ? 'true' : 'false'"
								data-testid="scene-on-air"
							>
								<span class="size-1 rounded-full" :class="sessionActive ? 'bg-white' : 'bg-surface-text-2'" aria-hidden="true" />{{ programLabel }}
							</span>
							<span v-if="tally(flow) === 'preview' || tally(flow) === 'both'" class="inline-flex h-4 shrink-0 items-center gap-1 whitespace-nowrap rounded bg-success px-1.5 text-[0.58rem] font-semibold leading-none tracking-wider text-white" data-testid="scene-in-preview">
								<span class="size-1 rounded-full bg-white" aria-hidden="true" />PREVIEW
							</span>
							<span v-if="switchingFlowId === flow._id" class="whitespace-nowrap">Switching…</span>
							<span v-else-if="isEmpty(flow) && !flow.active" class="whitespace-nowrap text-surface-7" data-testid="scene-empty">No sources</span>
						</span>
					</span>
				</button>
				</MediaflowSourcesTip>
				<MediaflowMenu v-if="!readOnly" :aria-label="`Actions for ${flow.name}`">
					<DropDownMenuItem v-if="!mobile" @click="emit('select', flow)">Show in Preview</DropDownMenuItem>
					<DropDownMenuItem :disabled="!enabled || flow.active || switchingFlowId === flow._id" @click="emit('send-to-live', flow)">
						{{ flow.active ? 'Already on Program' : sessionActive ? 'Send to live' : 'Start with this scene' }}
					</DropDownMenuItem>
					<DropDownMenuItem :disabled="!enabled" @click="emit('rename', flow)">Rename</DropDownMenuItem>
					<DropDownMenuItem :disabled="!!addReason" @click="emit('duplicate', flow)">Duplicate</DropDownMenuItem>
					<DropDownMenuItem :disabled="!enabled || i === 0" @click="emit('move', flow, -1)">Move up</DropDownMenuItem>
					<DropDownMenuItem :disabled="!enabled || i === scenes.length - 1" @click="emit('move', flow, 1)">Move down</DropDownMenuItem>
					<DropDownMenuItem :disabled="!enabled || flow.active" :custom-classes="DOCK_DANGER_ITEM" @click="emit('delete', flow)">
						{{ flow.active ? (sessionActive ? 'On air, can’t delete' : 'Cued, can’t delete') : 'Delete' }}
					</DropDownMenuItem>
				</MediaflowMenu>
			</li>
		</ul>
	</MediaflowDock>
</template>
